import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { PiArrowsClockwise, PiDotsThreeBold, PiPencilSimple, PiShareNetwork, PiTrash, PiUserSwitch } from 'react-icons/pi';
import { useFinancialPrivacy } from '../../FinancialPrivacyContext';
import {
  TRANSACTION_STATUS,
  TRANSACTION_TYPES,
  getAmountCents,
  isValidStatusForType,
} from '../../domain/finance';
import SensitiveMoney from '../SensitiveMoney';
import TransactionDateBadge from '../TransactionDateBadge';
import StatusConfirmation from './StatusConfirmation';
import BottomSheet from '../BottomSheet';
import { haptics } from '../../utils/haptics';
import { measureAnchoredPopover } from './popoverPosition';
import { getTransactionDisplayName } from '../../utils/transactionPresentation';
import { getTransactionAttributionLabel } from '../../domain/finance/memberAttribution';
import './styles.css';

const TransactionItem = ({
  item,
  showValues,
  onEdit,
  onDelete,
  onShare = null,
  onChangeAttribution = null,
  onToggleStatus,
  onOpenVirtual,
}) => {
  const privacy = useFinancialPrivacy();
  const valuesVisible = typeof showValues === 'boolean' ? showValues : privacy.valuesVisible;
  const [menuOpen, setMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [statusOpen, setStatusOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const menuTriggerRef = useRef(null);
  const menuPanelRef = useRef(null);
  const statusTriggerRef = useRef(null);
  const rootRef = useRef(null);
  const gestureRef = useRef(null);
  const swipeId = `${item.source || item.tipo || 'transaction'}-${item.id}`;
  const income = item.tipo === TRANSACTION_TYPES.INCOME;
  const isVirtual = item.isVirtual === true;
  const isRecurring = typeof item.occurrenceKey === 'string' && item.occurrenceKey.length > 0;
  const amountCents = getAmountCents(item);
  const isPending = item.status === TRANSACTION_STATUS.PENDING;
  const isValidStatus = isValidStatusForType(item.tipo, item.status);
  const statusClass = !isValidStatus ? 'invalid' : isPending ? 'pending' : 'realized';
  const statusLabel = !isValidStatus
    ? 'Status inválido'
    : isPending
      ? 'Pendente'
      : income
        ? 'Recebido'
        : 'Pago';
  const statusActionLabel = isPending
    ? income ? 'Marcar como recebida' : 'Marcar como paga'
    : 'Voltar para pendente';
  const statusActionCopy = isPending
    ? `Deseja marcar esta ${income ? 'receita como recebida' : 'despesa como paga'}?`
    : 'Deseja voltar esta movimentação para pendente?';
  const displayName = getTransactionDisplayName(item);

  const closeSwipe = useCallback(() => setSwipeOffset(0), []);
  const openSwipe = useCallback(() => {
    document.dispatchEvent(new CustomEvent('nf-transaction-swipe-open', { detail: swipeId }));
    setSwipeOffset(-144);
  }, [swipeId]);

  useEffect(() => {
    const closeOtherRows = (event) => {
      if (event.detail !== swipeId) closeSwipe();
    };
    const closeFromOutside = (event) => {
      if (swipeOffset && !rootRef.current?.contains(event.target)) closeSwipe();
    };
    document.addEventListener('nf-transaction-swipe-open', closeOtherRows);
    document.addEventListener('pointerdown', closeFromOutside);
    return () => {
      document.removeEventListener('nf-transaction-swipe-open', closeOtherRows);
      document.removeEventListener('pointerdown', closeFromOutside);
    };
  }, [closeSwipe, swipeId, swipeOffset]);

  const handlePointerDown = (event) => {
    if (!window.matchMedia('(max-width: 767px)').matches || event.button !== 0) return;
    gestureRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startOffset: swipeOffset,
      horizontal: false,
      cancelled: false,
      hapticSent: false,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId || gesture.cancelled) return;
    const dx = event.clientX - gesture.startX;
    const dy = event.clientY - gesture.startY;
    if (!gesture.horizontal && Math.abs(dx) + Math.abs(dy) > 8) {
      if (Math.abs(dy) > Math.abs(dx)) {
        gesture.cancelled = true;
        return;
      }
      gesture.horizontal = true;
    }
    if (!gesture.horizontal) return;
    event.preventDefault();
    const nextOffset = Math.max(-144, Math.min(0, gesture.startOffset + dx));
    gesture.currentOffset = nextOffset;
    setSwipeOffset(nextOffset);
    if (nextOffset <= -72 && !gesture.hapticSent) {
      gesture.hapticSent = true;
      void haptics.light();
    }
  };

  const handlePointerEnd = (event) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    gestureRef.current = null;
    if (!gesture.horizontal) return;
    if ((gesture.currentOffset ?? swipeOffset) <= -72) openSwipe();
    else closeSwipe();
  };

  const runSwipeAction = (action) => {
    closeSwipe();
    action();
  };

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setMenuPosition(null);
  }, []);

  const updateMenuPosition = useCallback(() => {
    const next = measureAnchoredPopover({
      anchor: menuTriggerRef.current,
      panel: menuPanelRef.current,
      preferredWidth: 260,
    });
    if (next) setMenuPosition(next);
  }, []);

  useLayoutEffect(() => {
    if (!menuOpen) return undefined;
    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [menuOpen, updateMenuPosition]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const closeOnOutside = (event) => {
      if (!menuTriggerRef.current?.contains(event.target) && !menuPanelRef.current?.contains(event.target)) closeMenu();
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') closeMenu();
    };
    document.addEventListener('mousedown', closeOnOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [closeMenu, menuOpen]);

  return (
    <li ref={rootRef} className={`transaction-item${swipeOffset ? ' is-swiped' : ''}`}>
      <div className="transaction-item__swipe-actions" aria-hidden={!swipeOffset}>
        <button type="button" className="is-edit" tabIndex={swipeOffset ? 0 : -1} onClick={() => runSwipeAction(() => onEdit(item))}><PiPencilSimple aria-hidden="true" /><span>Editar</span></button>
        <button type="button" className="is-more" tabIndex={swipeOffset ? 0 : -1} onClick={() => runSwipeAction(() => setMoreOpen(true))}><PiDotsThreeBold aria-hidden="true" /><span>Mais</span></button>
      </div>
      <div
        className="transaction-item__surface"
        role="group"
        aria-label={`Movimentação ${item.nome || item.name || ''}`.trim()}
        style={{ transform: `translate3d(${swipeOffset}px, 0, 0)` }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
      >
      <div className={`transaction-item__marker transaction-item__marker--${income ? 'income' : 'expense'}`} aria-hidden="true" />
      <TransactionDateBadge record={item} />
      <div className="transaction-item__content">
        <strong className="transaction-item__name">{displayName}</strong>
        <span className="transaction-item__category">{item.categoria || 'Sem categoria'} <span aria-label={`Atribuição: ${getTransactionAttributionLabel(item)}`}>· {getTransactionAttributionLabel(item)}</span>{isRecurring && <span className="transaction-item__recurring" aria-label="Recorrente" title="Recorrente"><PiArrowsClockwise aria-hidden="true" /></span>}</span>
      </div>
      <div className="transaction-item__trailing">
        <SensitiveMoney
          as="strong"
          className={`transaction-item__amount transaction-item__amount--${income ? 'income' : 'expense'} nf-financial-value`}
          valueCents={amountCents || 0}
          semantic={income ? 'income' : 'expense'}
          showSign
          variant="compact"
          visible={valuesVisible}
        />
        <button
          ref={statusTriggerRef}
          type="button"
          className={`transaction-item__status transaction-item__status--${statusClass}`}
          onClick={() => {
            closeMenu();
            if (isVirtual) {
              onOpenVirtual?.(item);
              return;
            }
            setStatusOpen((open) => !open);
          }}
          aria-expanded={statusOpen}
          aria-haspopup="dialog"
          aria-label={
            isVirtual
              ? 'Ver informações da ocorrência recorrente'
              : isPending
              ? income
                ? 'Marcar receita como recebida'
                : 'Marcar despesa como paga'
              : 'Marcar movimentação como pendente'
          }
        >
          {statusLabel}
        </button>
      </div>
      <div className="transaction-item__menu">
        <button
          ref={menuTriggerRef}
          type="button"
          className="transaction-item__menu-trigger"
          onClick={() => {
            setStatusOpen(false);
            setMenuOpen((open) => {
              setMenuPosition(null);
              return !open;
            });
          }}
          aria-label={`Ações de ${displayName}`}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
        >
          <PiDotsThreeBold aria-hidden="true" />
        </button>
      </div>
      </div>

      {menuOpen && createPortal(
        <div
          ref={menuPanelRef}
          className={`transaction-item__menu-panel transaction-item__menu-panel--${menuPosition?.placement || 'bottom'}`}
          style={menuPosition ? {
            left: menuPosition.left,
            top: menuPosition.top,
            width: menuPosition.width,
            maxHeight: menuPosition.maxHeight,
            visibility: 'visible',
            pointerEvents: 'auto',
          } : {
            left: '-10000px',
            top: 0,
            width: 260,
            maxHeight: 'none',
            visibility: 'visible',
            pointerEvents: 'none',
          }}
          role="menu"
          aria-label={`Ações de ${displayName}`}
        >
          <button type="button" role="menuitem" onClick={() => { closeMenu(); onEdit(item); }}><span className="transaction-item__menu-icon"><PiPencilSimple aria-hidden="true" /></span><span>Editar</span></button>
          {onShare && !isVirtual && <button type="button" role="menuitem" onClick={() => { closeMenu(); onShare(item); }}><span className="transaction-item__menu-icon"><PiShareNetwork aria-hidden="true" /></span><span>Compartilhar com família</span></button>}
          {onChangeAttribution && !isVirtual && <button type="button" role="menuitem" onClick={() => { closeMenu(); onChangeAttribution(item); }}><span className="transaction-item__menu-icon"><PiUserSwitch aria-hidden="true" /></span><span>Alterar atribuição</span></button>}
          <button type="button" role="menuitem" className="transaction-item__delete" onClick={() => { closeMenu(); onDelete(item); }}><span className="transaction-item__menu-icon"><PiTrash aria-hidden="true" /></span><span>Excluir</span></button>
        </div>,
        document.body
      )}

      {statusOpen && (
        <StatusConfirmation
          anchorRef={statusTriggerRef}
          item={item}
          currentStatus={statusLabel}
          actionLabel={statusActionLabel}
          actionCopy={statusActionCopy}
          onCancel={() => setStatusOpen(false)}
          onConfirm={() => onToggleStatus(item)}
        />
      )}
      {moreOpen && (
        <BottomSheet title="Mais ações" subtitle={displayName} onClose={() => setMoreOpen(false)} size="sm" className="transaction-item__more-sheet">
          <div className="transaction-item__more-actions">
            {onShare && !isVirtual && <button type="button" onClick={() => { setMoreOpen(false); onShare(item); }}><PiShareNetwork aria-hidden="true" /><span><strong>Compartilhar com Família</strong><small>Adicionar uma cópia ao espaço familiar</small></span></button>}
            {onChangeAttribution && !isVirtual && <button type="button" onClick={() => { setMoreOpen(false); onChangeAttribution(item); }}><PiUserSwitch aria-hidden="true" /><span><strong>Alterar atribuição</strong><small>Escolher uma pessoa ou Família</small></span></button>}
            <button type="button" className="is-danger" onClick={() => { setMoreOpen(false); onDelete(item); }}><PiTrash aria-hidden="true" /><span><strong>Excluir</strong><small>Será solicitada uma confirmação</small></span></button>
          </div>
        </BottomSheet>
      )}
    </li>
  );
};

TransactionItem.propTypes = {
  item: PropTypes.object.isRequired,
  showValues: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onShare: PropTypes.func,
  onChangeAttribution: PropTypes.func,
  onToggleStatus: PropTypes.func.isRequired,
  onOpenVirtual: PropTypes.func,
};

export default React.memo(TransactionItem);
